import { createContext } from 'react';
import type { OnboardingBranding, OnboardingProfile } from './onboardingWorkspace';

export type OnboardingDraft = {
  profile: OnboardingProfile;
  profileBase: OnboardingProfile;
  branding: OnboardingBranding;
  brandingBase: OnboardingBranding;
  revision: string;
};
export type OnboardingDraftRecovery = {
  read: () => OnboardingDraft | null;
  write: (draft: OnboardingDraft | null) => void;
};
export const OnboardingDraftRecoveryContext = createContext<OnboardingDraftRecovery | null>(null);
export const ONBOARDING_DRAFT_TTL_MS = 15 * 60_000;
const MAX_DRAFT_BYTES = 32_000;

// A single component-owned slot survives protected-view suspension. Rebinding or
// clearing invalidates old handles, including callbacks from interrupted requests.
export function createOnboardingDraftRecovery(now: () => number = Date.now) {
  let identity: string | null = null;
  let company: string | null = null;
  let handle: OnboardingDraftRecovery | null = null;
  let serialized: string | null = null;
  let expiresAt = 0;
  const clear = () => { identity = null; company = null; handle = null; serialized = null; expiresAt = 0; };
  return {
    clear,
    identify(actor: { id: string; auth_identity_id?: string; operations_grant_version?: number; is_modal_operations?: boolean }) {
      if (!actor.id || !actor.auth_identity_id || actor.is_modal_operations !== true ||
        !Number.isSafeInteger(actor.operations_grant_version) || actor.operations_grant_version! < 1) {
        clear(); return;
      }
      const next = JSON.stringify([actor.id, actor.auth_identity_id, actor.operations_grant_version]);
      if (identity !== next) { clear(); identity = next; }
    },
    bind(next: string | null): OnboardingDraftRecovery | null {
      if (company !== next) { company = next; handle = null; serialized = null; expiresAt = 0; }
      if (!identity || !company) return null;
      if (!handle) {
        const lease: OnboardingDraftRecovery = {
          read: () => {
            if (handle !== lease) return null;
            if (now() >= expiresAt) serialized = null;
            return serialized ? JSON.parse(serialized) as OnboardingDraft : null;
          },
          write: (draft) => {
            if (handle !== lease) return;
            const value = draft ? JSON.stringify(draft) : null;
            if (value && (value.length > MAX_DRAFT_BYTES || !/^[a-f0-9]{64}$/.test(draft!.revision))) {
              serialized = null; return;
            }
            if (value !== serialized) { serialized = value; expiresAt = now() + ONBOARDING_DRAFT_TTL_MS; }
          },
        };
        handle = lease;
      }
      return handle;
    },
  };
}
